import { BusyButton, LoadingStatus } from '../components/ui/LoadingFeedback';
import { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import type { FirstLoginChallenge } from '../contexts/AuthContext';
import { SessionRecovery } from '../components/auth/SessionRecovery';
import { environment } from '../config/environment';
import { useI18n } from '../contexts/I18nContext';
import { authApi } from '../lib/api';

interface FormErrors {
  email?: string;
  password?: string;
  newPassword?: string;
  confirmPassword?: string;
  givenName?: string;
  familyName?: string;
  recoveryCode?: string;
  general?: string;
}

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [firstLoginChallenge, setFirstLoginChallenge] = useState<FirstLoginChallenge | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [recoveryStep, setRecoveryStep] = useState<'request' | 'confirm' | 'complete' | null>(null);
  const [recoveryCode, setRecoveryCode] = useState('');

  const { isAuthenticated, isLoading, sessionError, login, completeNewPassword } = useAuth();
  const { t } = useI18n();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  // Clear errors when user starts typing
  useEffect(() => {
    if (errors.email && email) setErrors(prev => ({ ...prev, email: undefined }));
  }, [email, errors.email]);

  useEffect(() => {
    if (errors.password && password) setErrors(prev => ({ ...prev, password: undefined }));
  }, [password, errors.password]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingStatus />
      </div>
    );
  }

  if (sessionError) return <SessionRecovery />;

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!email.trim()) {
      newErrors.email = t('login.validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = t('login.validation.emailInvalid');
    }

    if (!password.trim()) {
      newErrors.password = t('login.validation.passwordRequired');
    } else if (password.length < 6) {
      newErrors.password = t('login.validation.passwordLength');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      const challenge = await login(email.trim(), password);
      if (challenge) {
        setFirstLoginChallenge(challenge);
        setPassword('');
      }
    } catch (err) {
      let errorMessage = t('login.error.default');

      if (err instanceof Error) {
        // Handle specific error messages
        if (err.message.includes('Invalid email or password')) {
          errorMessage = t('login.error.invalidCredentials');
        } else if (err.message.includes('Too many login attempts')) {
          errorMessage = t('login.error.tooManyAttempts');
        } else if (err.message.includes('Server error')) {
          errorMessage = t('login.error.server');
        } else if (err.message.includes('Network') || err.message.includes('fetch')) {
          errorMessage = t('login.error.network');
        } else if (err.message.length > 0) {
          errorMessage = err.message;
        }
      }

      setErrors({ general: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateNewPassword = (): boolean => {
    const newErrors: FormErrors = {};
    const meetsPolicy =
      newPassword.length >= 12 &&
      /[a-z]/.test(newPassword) &&
      /[A-Z]/.test(newPassword) &&
      /\d/.test(newPassword) &&
      /[^A-Za-z0-9]/.test(newPassword);

    if (!meetsPolicy) newErrors.newPassword = t('login.firstLogin.validation.policy');
    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = t('login.firstLogin.validation.mismatch');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstLoginChallenge || !validateNewPassword()) return;
    const userAttributes: { given_name?: string; family_name?: string } = {};
    if (firstLoginChallenge.requiredAttributes.includes('userAttributes.given_name')) {
      if (!givenName.trim()) {
        setErrors(prev => ({ ...prev, givenName: t('login.firstLogin.givenNameRequired') }));
        return;
      }
      userAttributes.given_name = givenName.trim();
    }
    if (firstLoginChallenge.requiredAttributes.includes('userAttributes.family_name')) {
      if (!familyName.trim()) {
        setErrors(prev => ({ ...prev, familyName: t('login.firstLogin.familyNameRequired') }));
        return;
      }
      userAttributes.family_name = familyName.trim();
    }

    setIsSubmitting(true);
    setErrors({});
    try {
      await completeNewPassword(firstLoginChallenge, newPassword, userAttributes);
    } catch (err) {
      setErrors({
        general:
          err instanceof Error &&
          err.message === 'Required profile information is missing or invalid'
            ? t('login.firstLogin.profileError')
            : err instanceof Error && err.message
              ? err.message
              : t('login.firstLogin.error'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const startPasswordRecovery = () => {
    setRecoveryStep('request');
    setFirstLoginChallenge(null);
    setPassword('');
    setErrors({});
  };

  const validateRecoveryEmail = (): boolean => {
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      setErrors({ email: t('login.validation.emailRequired') });
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setErrors({ email: t('login.validation.emailInvalid') });
      return false;
    }
    return true;
  };

  const requestRecoveryCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!validateRecoveryEmail()) return;
    setIsSubmitting(true);
    setErrors({});
    try {
      const response = await authApi.forgotPassword(email.trim());
      if (response.error) throw new Error(response.error.message);
      setRecoveryStep('confirm');
      setRecoveryCode('');
      setNewPassword('');
      setConfirmPassword('');
    } catch {
      setErrors({ general: t('login.recovery.requestError') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRecoveryConfirmation = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: FormErrors = {};
    const meetsPolicy =
      newPassword.length >= 12 &&
      /[a-z]/.test(newPassword) &&
      /[A-Z]/.test(newPassword) &&
      /\d/.test(newPassword) &&
      /[^A-Za-z0-9]/.test(newPassword);
    if (!recoveryCode.trim()) newErrors.recoveryCode = t('login.recovery.codeRequired');
    if (!meetsPolicy) newErrors.newPassword = t('login.firstLogin.validation.policy');
    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = t('login.firstLogin.validation.mismatch');
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length) return;

    setIsSubmitting(true);
    try {
      const response = await authApi.confirmForgotPassword({
        email: email.trim(),
        code: recoveryCode.trim(),
        newPassword,
      });
      if (response.error) throw new Error(response.error.message);
      setRecoveryStep('complete');
      setRecoveryCode('');
      setNewPassword('');
      setConfirmPassword('');
      setErrors({});
    } catch (err) {
      const message = err instanceof Error ? err.message.toLowerCase() : '';
      setErrors({
        general: message.includes('invalid or expired')
          ? t('login.recovery.invalidCode')
          : message.includes('policy')
            ? t('login.firstLogin.validation.policy')
            : t('login.recovery.confirmError'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const returnToSignIn = () => {
    setRecoveryStep(null);
    setRecoveryCode('');
    setNewPassword('');
    setConfirmPassword('');
    setErrors({});
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-end"></div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-100">
            <svg
              className="h-6 w-6 text-primary-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t(
              firstLoginChallenge
                ? 'login.firstLogin.title'
                : recoveryStep
                  ? 'login.recovery.title'
                  : 'login.title'
            )}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t(
              firstLoginChallenge
                ? 'login.firstLogin.subtitle'
                : recoveryStep
                  ? 'login.recovery.subtitle'
                  : 'login.subtitle'
            )}
          </p>
        </div>

        {recoveryStep === 'complete' ? (
          <div className="mt-8 space-y-6">
            <p
              role="status"
              className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800"
            >
              {t('login.recovery.success')}
            </p>
            <button type="button" className="btn-primary w-full" onClick={returnToSignIn}>
              {t('login.recovery.backToSignIn')}
            </button>
          </div>
        ) : (
          <form
            className="mt-8 space-y-6"
            onSubmit={
              firstLoginChallenge
                ? handleNewPasswordSubmit
                : recoveryStep === 'request'
                  ? requestRecoveryCode
                  : recoveryStep === 'confirm'
                    ? handleRecoveryConfirmation
                    : handleSubmit
            }
          >
            {environment.mockAuth && (
              <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-md">
                <p className="text-sm font-medium">{t('login.mockAuthEnabled')}</p>
                <p className="mt-1 text-sm break-all">
                  {t('login.mockAuthUse', {
                    email: environment.mockAuthEmail,
                    password: environment.mockAuthPassword,
                  })}
                </p>
              </div>
            )}

            {errors.general && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                <div className="flex">
                  <svg
                    className="h-5 w-5 text-red-400 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {errors.general}
                </div>
              </div>
            )}

            {!firstLoginChallenge && !recoveryStep ? (
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="form-label">
                    {t('login.email')}
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    className={`form-input ${errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                    placeholder={t('login.emailPlaceholder')}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    disabled={isSubmitting}
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                </div>

                <div>
                  <label htmlFor="password" className="form-label">
                    {t('login.password')}
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      className={`form-input pr-10 ${errors.password ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                      placeholder={t('login.passwordPlaceholder')}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isSubmitting}
                    >
                      {showPassword ? (
                        <svg
                          className="h-5 w-5 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="h-5 w-5 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                  )}
                </div>
              </div>
            ) : firstLoginChallenge ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">{t('login.firstLogin.instructions')}</p>
                {firstLoginChallenge.requiredAttributes.includes('userAttributes.given_name') && (
                  <div>
                    <label htmlFor="first-login-given-name" className="form-label">
                      {t('userManagement.givenName')}
                    </label>
                    <input
                      id="first-login-given-name"
                      aria-invalid={Boolean(errors.givenName)}
                      aria-describedby={
                        errors.givenName ? 'first-login-given-name-error' : undefined
                      }
                      type="text"
                      autoComplete="given-name"
                      className="form-input"
                      value={givenName}
                      onChange={event => {
                        setGivenName(event.target.value);
                        if (errors.givenName)
                          setErrors(prev => ({ ...prev, givenName: undefined }));
                      }}
                      disabled={isSubmitting}
                      required
                    />
                    {errors.givenName && (
                      <p id="first-login-given-name-error" className="mt-1 text-sm text-red-600">
                        {errors.givenName}
                      </p>
                    )}
                  </div>
                )}
                {firstLoginChallenge.requiredAttributes.includes('userAttributes.family_name') && (
                  <div>
                    <label htmlFor="first-login-family-name" className="form-label">
                      {t('userManagement.familyName')}
                    </label>
                    <input
                      id="first-login-family-name"
                      aria-invalid={Boolean(errors.familyName)}
                      aria-describedby={
                        errors.familyName ? 'first-login-family-name-error' : undefined
                      }
                      type="text"
                      autoComplete="family-name"
                      className="form-input"
                      value={familyName}
                      onChange={event => {
                        setFamilyName(event.target.value);
                        if (errors.familyName)
                          setErrors(prev => ({ ...prev, familyName: undefined }));
                      }}
                      disabled={isSubmitting}
                      required
                    />
                    {errors.familyName && (
                      <p id="first-login-family-name-error" className="mt-1 text-sm text-red-600">
                        {errors.familyName}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <label htmlFor="new-password" className="form-label">
                    {t('login.firstLogin.newPassword')}
                  </label>
                  <input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    className={`form-input ${errors.newPassword ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                    value={newPassword}
                    onChange={e => {
                      setNewPassword(e.target.value);
                      if (errors.newPassword)
                        setErrors(prev => ({ ...prev, newPassword: undefined }));
                    }}
                    disabled={isSubmitting}
                  />
                  {errors.newPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="confirm-password" className="form-label">
                    {t('login.firstLogin.confirmPassword')}
                  </label>
                  <input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    className={`form-input ${errors.confirmPassword ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                    value={confirmPassword}
                    onChange={e => {
                      setConfirmPassword(e.target.value);
                      if (errors.confirmPassword)
                        setErrors(prev => ({ ...prev, confirmPassword: undefined }));
                    }}
                    disabled={isSubmitting}
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                  )}
                </div>
                <button
                  type="button"
                  className="text-sm text-primary-600 hover:text-primary-500"
                  onClick={() => {
                    setFirstLoginChallenge(null);
                    setNewPassword('');
                    setConfirmPassword('');
                    setErrors({});
                  }}
                  disabled={isSubmitting}
                >
                  {t('login.firstLogin.back')}
                </button>
              </div>
            ) : recoveryStep === 'request' ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">{t('login.recovery.requestInstructions')}</p>
                <div>
                  <label htmlFor="email" className="form-label">
                    {t('login.email')}
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    className={`form-input ${errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                    placeholder={t('login.emailPlaceholder')}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    disabled={isSubmitting}
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                </div>
                <button
                  type="button"
                  className="text-sm text-primary-600 hover:text-primary-500"
                  onClick={returnToSignIn}
                  disabled={isSubmitting}
                >
                  {t('login.firstLogin.back')}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <p role="status" className="text-sm text-gray-600">
                  {t('login.recovery.codeSent')}
                </p>
                <div>
                  <label htmlFor="recovery-code" className="form-label">
                    {t('login.recovery.code')}
                  </label>
                  <input
                    id="recovery-code"
                    autoComplete="one-time-code"
                    className={`form-input ${errors.recoveryCode ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                    value={recoveryCode}
                    onChange={e => setRecoveryCode(e.target.value)}
                    disabled={isSubmitting}
                  />
                  {errors.recoveryCode && (
                    <p className="mt-1 text-sm text-red-600">{errors.recoveryCode}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="recovery-new-password" className="form-label">
                    {t('login.firstLogin.newPassword')}
                  </label>
                  <input
                    id="recovery-new-password"
                    type="password"
                    autoComplete="new-password"
                    className={`form-input ${errors.newPassword ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    disabled={isSubmitting}
                  />
                  {errors.newPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    {t('login.firstLogin.validation.policy')}
                  </p>
                </div>
                <div>
                  <label htmlFor="recovery-confirm-password" className="form-label">
                    {t('login.firstLogin.confirmPassword')}
                  </label>
                  <input
                    id="recovery-confirm-password"
                    type="password"
                    autoComplete="new-password"
                    className={`form-input ${errors.confirmPassword ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    disabled={isSubmitting}
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                  )}
                </div>
                <div className="flex items-center justify-between gap-4">
                  <button
                    type="button"
                    className="text-sm text-primary-600 hover:text-primary-500"
                    onClick={() => requestRecoveryCode()}
                    disabled={isSubmitting}
                  >
                    {t('login.recovery.resend')}
                  </button>
                  <button
                    type="button"
                    className="text-sm text-primary-600 hover:text-primary-500"
                    onClick={returnToSignIn}
                    disabled={isSubmitting}
                  >
                    {t('login.firstLogin.back')}
                  </button>
                </div>
              </div>
            )}

            <div>
              <BusyButton
                busy={isSubmitting}
                busyLabel={t('login.submitting')}
                type="submit"
                disabled={
                  isSubmitting ||
                  (firstLoginChallenge || recoveryStep === 'confirm'
                    ? !newPassword || !confirmPassword
                    : recoveryStep === 'request'
                      ? !email.trim()
                      : !email.trim() || !password.trim())
                }
                className="btn-primary w-full flex items-center justify-center"
              >
                {t(
                  firstLoginChallenge
                    ? 'login.firstLogin.submit'
                    : recoveryStep === 'request'
                      ? 'login.recovery.sendCode'
                      : recoveryStep === 'confirm'
                        ? 'login.recovery.resetPassword'
                        : 'login.submit'
                )}
              </BusyButton>
            </div>

            {!recoveryStep && !firstLoginChallenge && (
              <div className="text-center">
                <button
                  type="button"
                  className="text-sm text-primary-600 hover:text-primary-500"
                  onClick={startPasswordRecovery}
                >
                  {t('login.recovery.forgotPassword')}
                </button>
              </div>
            )}

            {!recoveryStep && (
              <div className="text-center">
                <p className="text-sm text-gray-600">{t('login.support')}</p>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};
